import { BoardNodeHandles } from "./board-node-handles";
import { NodeResizer, type NodeProps } from "@xyflow/react";
import { useCanvasEditability, useCanvasEditSession, useCanvasResizeSession } from "../../lib/canvas-editability";
import { patchNodeData } from "../../lib/canvas-commands";
import type { RectangleNodeData } from "../../types/board";
import { getContrastTextColor } from "../../lib/color-utils";

export function RectangleNode({ id, data, selected, isConnectable }: NodeProps) {
  const nodeData = data as unknown as RectangleNodeData;
  const { canEditDocument } = useCanvasEditability();
  const edit = useCanvasEditSession("text");
  const resize = useCanvasResizeSession();
  const updateLabel = (label: string) => {
    if (label !== (nodeData.label ?? "")) edit.update(patchNodeData(id, { label }));
  };

  const textColor = getContrastTextColor(nodeData.bgColor);
  const fontSize = nodeData.fontSize || 14;

  return (
    <>
      <NodeResizer
        {...resize}
        isVisible={!!selected && canEditDocument}
        minWidth={80}
        minHeight={40}
        lineClassName="!border-primary"
        handleClassName="!h-2.5 !w-2.5 !rounded-sm !border-2 !border-primary !bg-background"
      />
      <BoardNodeHandles type="rectangle" isConnectable={isConnectable} />
      <div
        className="flex h-full w-full items-center justify-center rounded-md border-2 px-4 py-3"
        style={{
          backgroundColor: nodeData.bgColor || "var(--color-card)",
          borderColor: nodeData.borderColor || "var(--color-border)",
          color: textColor,
          fontSize,
        }}
        onDoubleClick={edit.begin}
      >
        {edit.active ? (
          <input
            autoFocus
            className="nodrag nowheel w-full bg-transparent text-center outline-none"
            style={{ color: textColor, fontSize }}
            defaultValue={nodeData.label}
            onBlur={(e) => {
              updateLabel(e.target.value);
              edit.finish();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                updateLabel((e.target as HTMLInputElement).value);
                edit.finish();
              }
            }}
          />
        ) : (
          <span className="min-h-0 min-w-0 max-h-full w-full overflow-hidden whitespace-pre-wrap text-center [overflow-wrap:anywhere]">{nodeData.label ?? ""}</span>
        )}
      </div>
    </>
  );
}
